import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import cx from "clsx";
import { useEffect, useState } from "react";
import { FaPencilAlt } from "react-icons/fa";
import { ActionIcon, Box, Flex, Textarea, Tooltip } from "@mantine/core";
import { GraphQLQuery, generateClient } from "aws-amplify/api";
import { sortByReferenceIds } from "@/utils/utils";
import { updateTemplate } from "@/graphql/mutations";
import {
  UpdateTemplateInput,
  UpdateTemplateMutation,
  UpdateTemplateMutationVariables,
} from "@/API";
import classes from "./TemplateContent.module.css";
import EditFieldTemplateModal from "./TemplateModals/EditFieldTemplateModal";
import EditSectionTemplateModal from "./TemplateModals/EditSectionTemplateModal";
import EditSubSectionTemplateModal from "./TemplateModals/EditSubSectionTemplateModal";
import EditTextBoxTemplateModal from "./TemplateModals/EditTextBoxTemplateModal";
import ContentSubsectionTemplate from "./TemplateSection/ContentSubsectionTemplate";
import DetailTemplateSection from "./TemplateSection/DetailTemplateSection";

const client = generateClient();
/* eslint-disable @typescript-eslint/no-explicit-any */
const TemplateContent = ({ template, fetchTemplate }: any) => {
  const [sectionItems, setSectionItems] = useState<any>(template?.sections);
  const reorderSection = ({ from, to }: any) => {
    const sectionsClone = { ...sectionItems };
    const item = sectionItems.items[from];
    sectionItems.items.splice(from, 1);
    sectionItems.items.splice(to, 0, item);
    const idsOrder = sectionsClone.items.map((item: any) => {
      return { name: item.name, id: item.id };
    });
    setSectionItems(sectionsClone);
    changeSectionOrder(idsOrder.map((order: any) => order.id));
  };

  const [modalEditSectionOpened, setModalEditSectionOpened] = useState(false);
  const [modalEditSubSectionOpened, setModalEditSubSectionOpened] =
    useState(false);
  const [modalEditFieldOpened, setModalEditFieldOpened] = useState(false);
  const [modalEditTextBoxOpened, setModalEditTextBoxOpened] = useState(false);
  const [sectionEdit, setSectionEdit] = useState();
  const [subsectionEdit, setSubSectionEdit] = useState();
  const [fieldEdit, setFieldEdit] = useState();
  const [textBoxEdit, setTextBoxEdit] = useState();

  const changeSectionOrder = async (newSectionOrderIds: any) => {
    // Use your GraphQL client to make a mutation call
    const updatedTemplateInput: UpdateTemplateInput = {
      id: template.id,
      _version: template._version,
      sectionOrder: newSectionOrderIds, // change this field to overrite in schema??
    };
    const updatedTemplateVars: UpdateTemplateMutationVariables = {
      input: updatedTemplateInput,
    };
    try {
      // dispatch(setIsRefreshing());
      await client.graphql<GraphQLQuery<UpdateTemplateMutation>>({
        query: updateTemplate,
        variables: updatedTemplateVars,
      });
      fetchTemplate(template.id);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (template) {
      const lstSections = sortByReferenceIds(
        template?.sections?.items,
        template?.sectionOrder,
      );
      template["sections"]["items"] = lstSections;
      setSectionItems({ ...template?.sections });
      setSectionItems({ ...template?.sections });
    }
  }, [template]);

  const items = sectionItems?.items?.map((item: any, index: number) => (
    <Draggable
      key={`draggable-section-${item.id}`}
      index={index}
      draggableId={item.id}
    >
      {(provided, snapshot) => (
        <Box
          key={`box-${item.id}`}
          className={cx({
            [classes.itemDragging]: snapshot.isDragging,
            [classes.itemDragging]: snapshot.isDragging,
          })}
          ref={provided.innerRef}
          {...provided.draggableProps}
        >
          <DetailTemplateSection
            title={item.name}
            key={`detail-template-${item.id}`}
            provided={provided}
            onEditSection={() => {
              setSectionEdit(item);
              setModalEditSectionOpened(true);
            }}
          >
            <ContentSubsectionTemplate
              section={item}
              templateId={template?.id}
              fetchTemplate={fetchTemplate}
              setSubSectionEdit={setSubSectionEdit}
              setModalEditSubSectionOpened={setModalEditSubSectionOpened}
              setFieldEdit={setFieldEdit}
              setModalEditFieldOpened={setModalEditFieldOpened}
            />
            {item?.textboxes?.items?.map((textbox: any) => (
              <Flex key={textbox.id} flex={1} p={8}>
                <Flex
                  flex={1}
                  align={"center"}
                  ta={"right"}
                  justify={"space-between"}
                >
                  <Textarea
                    flex={1}
                    autosize
                    readOnly
                    variant="unstyled"
                    fz={14}
                    value={textbox.value}
                    ta={"right"}
                  />
                  <Tooltip label="Edit textbox" withArrow>
                    <ActionIcon
                      bg={"transparent"}
                      onClick={() => {
                        setTextBoxEdit({ ...textbox, sectionId: item.id });
                        setModalEditTextBoxOpened(true);
                      }}
                    >
                      <FaPencilAlt size={14} color="black" />
                    </ActionIcon>
                  </Tooltip>
                </Flex>
              </Flex>
            ))}
          </DetailTemplateSection>
        </Box>
      )}
    </Draggable>
  ));
  return (
    <>
      <DragDropContext
        onDragEnd={({ destination, source }) => {
          reorderSection({
            from: source.index,
            to: destination?.index || 0,
          });
        }}
      >
        <Droppable droppableId="dnd-list" direction="vertical">
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className="template-item"
            >
              {items}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      <EditSectionTemplateModal
        fetchTemplate={fetchTemplate}
        modalOpened={modalEditSectionOpened}
        setModalOpened={setModalEditSectionOpened}
        template={template}
        sectionEdit={sectionEdit}
      />
      <EditSubSectionTemplateModal
        fetchTemplate={fetchTemplate}
        modalOpened={modalEditSubSectionOpened}
        setModalOpened={setModalEditSubSectionOpened}
        template={template}
        subsection={subsectionEdit}
      />
      <EditFieldTemplateModal
        fetchTemplate={fetchTemplate}
        modalOpened={modalEditFieldOpened}
        setModalOpened={setModalEditFieldOpened}
        template={template}
        field={fieldEdit}
      />
      <EditTextBoxTemplateModal
        fetchTemplate={fetchTemplate}
        modalOpened={modalEditTextBoxOpened}
        setModalOpened={setModalEditTextBoxOpened}
        template={template}
        textbox={textBoxEdit}
      />
    </>
  );
};

export default TemplateContent;
